import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, tap, throwError } from 'rxjs';
import { IS_REFRESH_TOKEN } from '../auth/auth.interceptor';
import { envelopeFailed, envelopeMessage } from '../envelope';
import { AppError } from '../models/app-error.model';
import { UiActions } from '../state/ui/ui.actions';

export const SKIP_TOAST = new HttpContextToken<boolean>(() => false);

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Surfaces Identity/Ops envelope `message` values as toasts.
 * Placed outside the refresh interceptor so a recovered 401 is not toasted.
 */
export const toastInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);

  if (req.context.get(SKIP_TOAST) || req.context.get(IS_REFRESH_TOKEN)) {
    return next(req);
  }

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) {
        return;
      }
      const message = envelopeMessage(event.body);
      if (!message) {
        return;
      }
      if (envelopeFailed(event.body)) {
        store.dispatch(
          UiActions.showToast({ severity: 'error', summary: 'Error', detail: message }),
        );
        return;
      }
      if (MUTATING_METHODS.has(req.method.toUpperCase())) {
        store.dispatch(
          UiActions.showToast({ severity: 'success', summary: 'Success', detail: message }),
        );
      }
    }),
    catchError((error: unknown) => {
      if (!shouldSkipErrorToast(req.url, error)) {
        const message = userFacingErrorMessage(error);
        if (message) {
          store.dispatch(
            UiActions.showToast({ severity: 'error', summary: 'Error', detail: message }),
          );
        }
      }
      return throwError(() => error);
    }),
  );
};

function shouldSkipErrorToast(url: string, error: unknown): boolean {
  if (!isUnauthorized(error)) {
    return false;
  }
  return !url.includes('/api/v1/auth/login');
}

function isUnauthorized(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    return error.status === 401;
  }
  return (error as AppError)?.code === '401';
}

function userFacingErrorMessage(error: unknown): string | undefined {
  if (error instanceof HttpErrorResponse) {
    return envelopeMessage(error.error) ?? networkFallback(error);
  }

  const message = (error as AppError)?.message;
  if (
    typeof message === 'string' &&
    message.trim() &&
    !message.toLowerCase().startsWith('http failure')
  ) {
    return message.trim();
  }

  return undefined;
}

function networkFallback(error: HttpErrorResponse): string | undefined {
  return error.status === 0 ? 'Unable to reach the server. Please try again.' : undefined;
}
